// Actions for managing background processes

export const addProcess = (processData) => {
    // Generate unique ID with timestamp
    const processId = `process-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    return {
        type: 'ADD_PROCESS',
        payload: {
            id: processId,
            name: processData.name,
            type: processData.type, // e.g., 'audio_mastering', 'mixing', 'enhancement'
            status: 'processing', // processing, completed, failed
            progress: 0, // 0-100
            startedAt: new Date().toISOString(),
            completedAt: null,
            error: null,
            result: null,
            redirectPath: processData.redirectPath, // Path to redirect when clicked in notification
            metadata: processData.metadata || {}, // Any additional data
        },
    };
};

export const updateProcess = (processId, updates) => {
    return {
        type: 'UPDATE_PROCESS',
        payload: {
            id: processId,
            updates,
        },
    };
};

export const updateProcessProgress = (processId, progress) => {
    return {
        type: 'UPDATE_PROCESS',
        payload: {
            id: processId,
            updates: { progress: Math.min(progress, 100) },
        },
    };
};

export const completeProcess = (processId) => {
    return {
        type: 'COMPLETE_PROCESS',
        payload: processId,
    };
};

export const failProcess = (processId, error) => {
    return {
        type: 'FAIL_PROCESS',
        payload: {
            id: processId,
            error,
        },
    };
};

export const removeProcess = (processId) => {
    return {
        type: 'REMOVE_PROCESS',
        payload: processId,
    };
};

export const setCurrentProcessId = (processId) => {
    return {
        type: 'SET_CURRENT_PROCESS_ID',
        payload: processId,
    };
};

export const clearCurrentProcessId = () => {
    return {
        type: 'CLEAR_CURRENT_PROCESS_ID',
    };
};

export const markNotificationRead = (notificationId) => {
    return {
        type: 'MARK_NOTIFICATION_READ',
        payload: notificationId,
    };
};

export const markAllNotificationsRead = () => {
    return {
        type: 'MARK_ALL_NOTIFICATIONS_READ',
    };
};

export const removeNotification = (notificationId) => {
    return {
        type: 'REMOVE_NOTIFICATION',
        payload: notificationId,
    };
};

export const clearCompletedProcesses = () => {
    return {
        type: 'CLEAR_COMPLETED_PROCESSES',
    };
};

export const clearAllNotifications = () => {
    return {
        type: 'CLEAR_ALL_NOTIFICATIONS',
    };
};

const isMongoId = (value) => typeof value === 'string' && /^[a-fA-F0-9]{24}$/.test(value);

export const addNotification = (notification) => {
    return {
        type: 'ADD_NOTIFICATION',
        payload: {
            id: notification.id || `notif-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            processId: notification.processId || null,
            message: notification.message,
            type: notification.type || 'success',
            read: false,
            createdAt: new Date().toISOString(),
            redirectPath: notification.redirectPath || null,
            rewardId: notification.rewardId || null,
        },
    };
};

const REWARD_CLAIM_PATH = '/admin/professor-u/rewards';

const resolveRewardId = (milestone) => {
    if (isMongoId(milestone?.rewardId)) return milestone.rewardId;
    if (isMongoId(milestone?.id)) return milestone.id;
    return null;
};

const getRewardIdsFromNotifications = (items = []) => [
    ...new Set(
        items
            .map((item) => item?.rewardId)
            .filter((id) => isMongoId(id))
    ),
];

export const notifyRewardCompletions = (milestones = []) => (dispatch, getState) => {
    const list = Array.isArray(milestones) ? milestones.filter(Boolean) : [];
    if (!list.length) return;

    const existing = getState()?.backgroundProcesses?.notifications || [];

    list.forEach((milestone) => {
        const rewardId = resolveRewardId(milestone);
        const key = rewardId || milestone.activityType || milestone.id || milestone.title;
        if (!key) return;

        const notificationId = `reward-${key}`;
        if (existing.some((n) => n.id === notificationId || (rewardId && n.rewardId === rewardId))) return;

        const title = milestone.title || 'Reward unlocked';
        dispatch(addNotification({
            id: notificationId,
            rewardId,
            message: `Reward unlocked: ${title}. Click to claim.`,
            type: 'success',
            redirectPath: REWARD_CLAIM_PATH,
        }));
    });
};

export const consumePendingRewardNotifications = (milestones = []) => (dispatch) => {
    const list = Array.isArray(milestones) ? milestones.filter(Boolean) : [];
    if (!list.length) return;
    dispatch(notifyRewardCompletions(list));
};

export const acknowledgeRewardNotifications = (notifications = []) => async () => {
    const rewardIds = getRewardIdsFromNotifications(notifications);
    if (!rewardIds.length) return;

    try {
        const { markRewardNotificationsSeen } = await import('../../api/professoru/forum.api');
        await markRewardNotificationsSeen(rewardIds);
    } catch (error) {
        console.error('Failed to mark reward notifications as seen', error);
    }
};
